import React, { useCallback, useContext, useLayoutEffect, useState } from "react";
import CareScreener from "../components/care-screener/CareScreener";
import { useSearchParams } from "react-router";
import { CareScreenerContext } from "@context/CareScreenerContext";
import { InsuranceEligibilityStatus } from "src/services/CareScreenerService";
import CareScreenerBackground from "@components/care-screener/CareScreenerBackground";
import CareScreenerCard from "@components/care-screener/CareScreenerCard";
import { Text } from "@core";
import { useActions } from "tiny-atom/react/hooks";
import { ScreenerView } from "../model/careScreenerModels";
import {
  IS_DEV,
  EVERNORTH_TRIGGER_PARAM,
  EVERNORTH_TRIGGER_VALUE,
  CONSULT_TYPEFORM_ID,
  EVERNORTH_TYPEFORM_ID
} from "@utils/constants";

const CONSULT_TYPEFORM_PATH = "consult";
const EVERNORTH_TYPEFORM_PATH = "evernorth";

const CONTINUE_TO_BOOK_CONSULT_QUESTION_REF = "endScreen-fieldRef";
const CONTINUE_TO_BOOK_CONSULT_QUESTION_REF_ALT = "phoneNumber-fieldRef";

const CARE_SCREENER_ID_REQUIRED_VIEWS: string[] = [ScreenerView.INSURANCE_ELIGIBILITY, ScreenerView.PASSWORD_INPUT];

const INVALID_LINK_MESSAGE = `Invalid link. Please ensure you're using the full link sent to you. For assistance, email support@getmanatee.com.`;

const ACCEPTED_CONTINUE_EXISTING_SCREENER_VIEWS: string[] = [
  ScreenerView.TYPEFORM_SUBMISSION,
  ...CARE_SCREENER_ID_REQUIRED_VIEWS
];

export default function ConsultPage() {
  const [routeSearchParams] = useSearchParams();

  const view = routeSearchParams.get("view");
  const typeformResponseId = routeSearchParams.get("responseId");
  const careScreenerSubmissionId = routeSearchParams.get("submissionId");
  const insuranceEligibilityStatus = routeSearchParams.get("insuranceStatus");

  const isEverNorthTypeform = routeSearchParams.get(EVERNORTH_TRIGGER_PARAM)?.toLowerCase() === EVERNORTH_TRIGGER_VALUE;

  const typeformId = isEverNorthTypeform ? EVERNORTH_TYPEFORM_ID : CONSULT_TYPEFORM_ID;
  const typeformPath = isEverNorthTypeform ? EVERNORTH_TYPEFORM_PATH : CONSULT_TYPEFORM_PATH;

  const { setInsuranceEligibilityStatus } = useContext(CareScreenerContext)!;
  const { setConsultView, setCareScreenerSubmissionId, setTypeformResponseId } = useActions();
  const [error, setError] = useState<string | undefined>(undefined);

  const handleContinueExistingScreener = useCallback(() => {
    if (view) {
      if (!ACCEPTED_CONTINUE_EXISTING_SCREENER_VIEWS.includes(view)) {
        setError(IS_DEV ? `Dev mode: View not supported: ${view}` : INVALID_LINK_MESSAGE);
        return;
      }
      if (!typeformResponseId) {
        setError(IS_DEV ? `Dev mode: responseID param required` : INVALID_LINK_MESSAGE);
        return;
      }
      if (CARE_SCREENER_ID_REQUIRED_VIEWS.includes(view) && !careScreenerSubmissionId) {
        setError(IS_DEV ? `Dev mode: submissionId param required for view: ${view}` : INVALID_LINK_MESSAGE);
        return;
      }

      setConsultView(view);
      if (careScreenerSubmissionId) {
        setCareScreenerSubmissionId(parseInt(careScreenerSubmissionId));
      }
      setTypeformResponseId(typeformResponseId);
      setInsuranceEligibilityStatus(insuranceEligibilityStatus as InsuranceEligibilityStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    handleContinueExistingScreener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <CareScreenerBackground>
        <CareScreenerCard>
          <Text center>{error}</Text>
        </CareScreenerCard>
      </CareScreenerBackground>
    );
  }

  return (
    <CareScreener
      typeformId={typeformId}
      typeformPath={typeformPath}
      allowAppointmentScheduling={true}
      accountCreationPredicate={{
        tfQuestionRefShown: CONTINUE_TO_BOOK_CONSULT_QUESTION_REF,
        tfQuestionRefShownAlt: CONTINUE_TO_BOOK_CONSULT_QUESTION_REF_ALT
      }}
      insuranceEligiblityCheckEnabled={true}
    />
  );
}
